(ns app.domain.verdict.model
  (:require #?(:clj [app.domain.user.authority :as user-authority])
            [app.domain.group.config.model :as config-model]
            [app.domain.group.model :as group-model]
            [app.domain.util :as du]
            [app.domain.verdict.section :as verdict-section]
            [core.db.child-entity :as child]
            [core.db.entity.util :as eu]
            [potpuri.core :as p]))

(def is-verdict?
  (partial eu/is-type? :entity.type/verdict))

(def states
  #{:verdict.state/permit-issued
    :verdict.state/permit-generating
    :verdict.state/awaiting-preconditions
    :verdict.state/awaiting-sign-off ; state in which the applicant can review and sign the permit
    :verdict.state/draft
    :verdict.state/expired ; related licence has expired
    :verdict.state/revoked})

(def post-issue-states
  "States that are only available for verdicts that have been issued before"
  #{:verdict.state/permit-issued
    :verdict.state/revoked
    :verdict.state/expired})

(def dormancy-notification-roles #{:role/applicant :role/property-owner})

(def permit-sign-off-roles [:role/applicant :role/agent])

(defn has-uploaded-signature? [verdict]
  (->> verdict
       :entity/_parent
       (map :file/category)
       (some (partial = :file.category/signed-verdict))))

(def ^{:arglists '([application])} application-verdicts
  (child/child-entities-of-type-fn :entity.type/verdict))

(def ^{:arglists '([verdict])} verdict-or-template-sections
  (child/child-entities-of-type-fn :entity.type/verdict-section))

(defn find-section
  "Returns the first verdict section with the matching criteria"
  [verdict criteria]
  (-> verdict eu/descendants (p/find-first criteria)))

(defn single-verdict [application]
  (let [verdicts (application-verdicts application)]
    (assert (<= (count verdicts) 1) "There are multiple verdicts under the application, cannot return singular verdict.")
    (first verdicts)))

(defn verdicts-signed? [application]
  (->> (application-verdicts application)
       (filter :verdict/sign-off-required?)
       (every? (some-fn :verdict/sign-off-by
                        has-uploaded-signature?))))

(defn verdict-in-state [target states]
  (some->> (application-verdicts target)
           (filter (apply du/in-state-fn states))
           first))

(defn issued-or-first-verdict [target]
  (or (verdict-in-state target [:verdict.state/permit-issued])
      (-> target application-verdicts first)))

(defmethod group-model/get-authority-group :entity.type/verdict
  [verdict]
  (-> verdict :entity/parent group-model/get-authority-group))

(defmethod group-model/get-authority-group :entity.type/verdict-section
  [verdict-section]
  (some-> verdict-section :entity/parent group-model/get-authority-group))

(defmethod group-model/get-authority-group :entity.type/verdict-template
  [verdict-template]
  (:domain/authority-group verdict-template))

(defn get-workspace [verdict]
  (-> verdict
      ;; application
      :entity/parent
      ;; workspace
      :entity/parent))

(defn non-draft-verdict [target]
  (some->> (application-verdicts target)
           (remove (du/in-state-fn :verdict.state/draft))
           first))

(defn verdict-or-licence-number
  "For licencing, in addition to the permit/verdict number there is the :licence/id of
  the licence entity itself. Normally these are the same number but for renewals the licence number
  is the same as on the original licence (unless otherwise configured) and the verdict-number is
  the same as the renewal's municipal-case-id."
  [verdict]
  (let [workspace (get-workspace verdict)
        licence   (first (:licence/_workspaces workspace))]
    (if (or (nil? licence)
            ;; Licence number configured to be same as verdict number after issuance so anticipate it for drafts
            (and ((du/in-state-fn :verdict.state/draft
                                  :verdict.state/awaiting-preconditions
                                  :verdict.state/awaiting-sign-off) verdict)
                 (-> workspace
                     config-model/resolve-municipal-case-id-source-for-ws
                     (#{:case-id-source/custom-id
                        :case-id-source/manual}))
                 (config-model/resolve-municipal-case-id-value :group.config/municipal-case-id-new-number-on-renewal?
                                                               :group/municipal-case-id-new-number-on-renewal?
                                                               workspace)))
      (:verdict/municipal-permit-number verdict)
      (:licence/id licence))))


(defn- condition-section-enabled-for-ws?
  [workspace]
  (or (nil? workspace)
      (config-model/resolve-include-conditions-in-verdict workspace)))

(defmulti exclude-section-by-type (fn [_ctx verdict-section]
                                    (:verdict.section/type verdict-section)))

(defmethod exclude-section-by-type :default
  [_ _]
  false)

(defmethod exclude-section-by-type :verdict.section.type/inspections
  [{:keys [group]} _]
  (not (group-model/has-feature? group :group.feature/inspections)))

(defmethod exclude-section-by-type :verdict.section.type/permit-specific-conditions
  [{:keys [group application]} _]
  (not (and (group-model/has-feature? group :group.feature/conditions)
            (condition-section-enabled-for-ws? (:entity/parent application)))))

(defmethod exclude-section-by-type :verdict.section.type/general-conditions
  [{:keys [group application]} _]
  (not (and (group-model/has-feature? group :group.feature/general-conditions)
            (condition-section-enabled-for-ws? (:entity/parent application)))))


(defmulti exclude-section-by-label (fn [_ctx verdict-section]
                                     (:verdict.section/label verdict-section)))

(defmethod exclude-section-by-label :default
  [_ _]
  false)

(defmethod exclude-section-by-label ":verdict.page/inspections"
  [{:keys [group]} _]
  (not (group-model/has-feature? group :group.feature/inspections)))

(defmethod exclude-section-by-label ":verdict.page/conditions"
  [{:keys [group application]} _]
  (not (and (group-model/has-feature? group :group.feature/conditions)
            (condition-section-enabled-for-ws? (:entity/parent application)))))

(defn template-used-in-express-issuance?
  "Returns true if the template is referenced by any group config as an express issuance template."
  [template]
  (-> template
      :group.config/_express-issuance-template
      seq
      boolean))

(defn template-structurally-eligible-for-express-issuance?
  "A template is structurally eligible for express issuance if its input sections are either already static for
   express issuance purposes, can have defaults configured, or are filled automatically on issuance.
   `input-sections` should be the template's descendant sections that are input-types."
  [input-sections]
  (every? (fn [{:verdict.section/keys [type]}]
            (not (contains? verdict-section/input-types-blocking-express-issuance type)))
          input-sections))

(defn verdict-default-config-valid?
  "Returns true if the verdict default config is valid."
  [{:verdict-default-config/keys [applies-to authority-user section-id] :as config}]
  (and (keyword? section-id)
       (case applies-to
         :verdict.section.type/date-input
         (let [{:verdict-default-config.date-input/keys [rule amount years]} config]
           (and (some? rule)
                (if (= rule :verdict-default-config.date-input.rule/day-of-year)
                  (int? years)
                  (int? amount))))

         :verdict.section.type/authority-user
         (and (some? authority-user)
              #?(:clj (user-authority/authority-user? authority-user)
                 :cljs true))

         true)))

(defn template-express-issuance-defaults-covered?
  "Returns true when all defaultable input sections are covered by valid verdict defaults
   matched by section-id + applies-to type."
  [input-sections verdict-defaults]
  (every? (fn [{:verdict.section/keys [id type]}]
            (or (not (contains? verdict-section/verdict-sections-with-defaults type))
                (some (fn [config]
                        (let [{:verdict-default-config/keys [section-id applies-to]} config]
                          (and (= section-id id)
                               (= applies-to type)
                               (verdict-default-config-valid? config))))
                      verdict-defaults)))
          input-sections))

(defn template-eligible-for-express-issuance?
  "A template is eligible for saving/using express issuance if unsupported input sections
   are absent and all defaultable input sections are currently covered by defaults."
  [input-sections verdict-defaults]
  (and (template-structurally-eligible-for-express-issuance? input-sections)
       (template-express-issuance-defaults-covered? input-sections verdict-defaults)))
